import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import PanelState, { extractApiError } from "@/components/PanelState";
import { PhoneCall } from "lucide-react";
import { CallTrackingEditor } from "./hiring/CallTrackingEditor";

// CC-301 — Hiring: pending applicants (unused intake links) with call tracking. Read-only over tokens.
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

function ApplicantRow({ a, slug }) {
  return (
    <tr className="border-t border-[#eef1f6] align-top" data-testid={`pending-row-${a.key}`}>
      <td className="py-2 px-3">
        <div className="font-semibold text-[#0e1420]">{a.name || <span className="text-[#9ca3af]">(no name)</span>}</div>
        {a.applied_count > 1 && (
          <div className="text-[11px] text-[#8a5a1a]" data-testid={`pending-applied-count-${a.key}`}>applied {a.applied_count} times</div>
        )}
        {a.candidate_id && (
          <Link to={`/${slug}/app/hiring/candidates/${a.candidate_id}`} className="text-[11px] text-[#0072bc] underline"
            data-testid={`pending-candidate-link-${a.key}`}>Already a candidate</Link>
        )}
      </td>
      <td className="py-2 px-3 text-xs break-all">{a.email || "—"}</td>
      <td className="py-2 px-3 text-xs whitespace-nowrap">{a.phone || "—"}</td>
      <td className="py-2 px-3 text-xs whitespace-nowrap">{fmtDate(a.applied_at)}</td>
      <td className="py-2 px-3 text-xs text-right font-mono-data" data-testid={`pending-days-${a.key}`}>{a.days_waiting ?? "—"}</td>
      <td className="py-2 px-3 min-w-[420px]">
        <CallTrackingEditor trackKey={a.key} initial={a.call_tracking} testId={`pending-call-${a.key}`} />
      </td>
    </tr>
  );
}

export default function HiringPending() {
  const { slug } = useParams();
  const [hidePlaceholders, setHidePlaceholders] = useState(true);
  const [showCandidates, setShowCandidates] = useState(false);
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/hiring/pending-applicants", {
        params: { include_placeholders: !hidePlaceholders, include_candidates: showCandidates },
      });
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: extractApiError(e), data: null });
    }
  }, [hidePlaceholders, showCandidates]);
  useEffect(() => { load(); }, [load]);

  const rows = state.data?.applicants || [];
  return (
    <div className="p-6 space-y-4" data-testid="hiring-pending-page">
      <div className="flex items-center gap-2">
        <PhoneCall className="w-5 h-5 text-[#0072bc]" />
        <h1 className="text-xl font-semibold text-[#0e1420]">Pending applicants</h1>
        <span className="text-xs text-[#6b7280]" data-testid="pending-count">{state.data ? `${state.data.count} shown` : ""}</span>
      </div>
      <p className="text-xs text-[#4b5563] max-w-3xl">
        People who were sent an intake link but haven't filled it in. One row per email (latest application). Nothing here sends email.
      </p>
      <div className="flex flex-wrap gap-4 text-xs">
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={hidePlaceholders} onChange={(e) => setHidePlaceholders(e.target.checked)} data-testid="pending-hide-placeholders" />
          Hide entries with no name, email or phone{state.data?.hidden_placeholders ? ` (${state.data.hidden_placeholders} hidden)` : ""}
        </label>
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={showCandidates} onChange={(e) => setShowCandidates(e.target.checked)} data-testid="pending-show-candidates" />
          Show people who are already candidates{state.data?.hidden_candidates ? ` (${state.data.hidden_candidates} hidden)` : ""}
        </label>
      </div>
      <PanelState status={state.loading ? "loading" : state.error ? "error" : "ok"} error={state.error} isEmpty={!rows.length}
        onRetry={load} emptyTitle="No pending applicants" testIdPrefix="pending" />
      {!state.loading && !state.error && rows.length > 0 && (
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="pending-table">
            <thead className="text-[10px] uppercase tracking-[.08em] text-[#6b7280] bg-[#f9fafb]">
              <tr>
                <th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">Email</th>
                <th className="text-left py-2 px-3">Phone</th><th className="text-left py-2 px-3">Applied</th>
                <th className="text-right py-2 px-3">Days waiting</th><th className="text-left py-2 px-3">Call tracking</th>
              </tr>
            </thead>
            <tbody>{rows.map((a) => <ApplicantRow key={a.key} a={a} slug={slug} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
